import {
  type DropdownOption,
  type GetOptions,
  type IntegrationRecordField,
} from '../../../../javascripts/shared-types';
import {
  type JiraIssueCreateMetaResponse,
  type JiraIssuePicker,
  type JiraUserSearch,
} from '../types';
import { OMIT_FIELDS } from './constants';

export interface TransformContext {
  getAutocompleteOptions: (data: {
    responseType: 'issuePicker' | 'user';
    autoCompleteUrl: string;
    searchParam?: string;
  }) => ReturnType<GetOptions>;
  getParentOptions: (data: { searchParam?: string }) => ReturnType<GetOptions>;
}

const getFieldType = (
  field: JiraIssueCreateMetaResponse['fields'][0],
  context: TransformContext
): IntegrationRecordField['type'] | undefined => {
  if (field.key === 'labels') {
    // this comes with an autocompleteUrl but it requires a different scope to what we have
    // so can't use it
    return {
      type: 'tags',
    };
  }

  if (field.key === 'parent') {
    return {
      type: 'select',
      multiple: false,
      options: ({ searchParam }) => context.getParentOptions({ searchParam }),
    };
  }

  if (
    field.schema.custom === 'com.pyxis.greenhopper.jira:gh-lexo-rank' ||
    field.schema.custom === 'com.pyxis.greenhopper.jira:gh-epic-link'
  ) {
    console.warn('Unsupported field', field);
    return;
  }

  switch (field.schema.type) {
    case 'string':
    case 'any':
      return {
        type: 'text',
      };
    case 'number':
      return {
        type: 'number',
      };
    case 'date':
    case 'datetime':
      return {
        type: field.schema.type,
      };
    case 'array':
      if (field.key === 'issuelinks' && field.autoCompleteUrl) {
        // TODO this needs to somehow handle the link type too, which will need to be retrieved from the API
        if (process.env.NODE_ENV !== 'development') {
          return;
        }

        return {
          type: 'select',
          // computer says no https://jira.atlassian.com/browse/JRASERVER-66329
          multiple: false,
          options: ({ searchParam }) =>
            context.getAutocompleteOptions({
              responseType: 'issuePicker',
              autoCompleteUrl: field.autoCompleteUrl,
              searchParam,
            }),
        };
      }

      if (!field.allowedValues || !field.allowedValues.length) {
        console.warn('No allowedValues', field);
        return;
      }

      return {
        type: 'select',
        multiple: true,
        options: field.allowedValues.map(value => ({
          value: value.id,
          text: value.name ?? value.value,
        })),
      };

    case 'priority':
    case 'option':
    case 'option-with-child': // TODO this is a nested list, need to handle it
    case 'version':
      if (!field.allowedValues || !field.allowedValues.length) {
        console.warn('No allowedValues', field);
        return;
      }

      return {
        type: 'select',
        multiple: false,
        // assuming allowedValues is always present for now
        options: field.allowedValues.map(value => ({
          value: value.id,
          text: value.name ?? value.value,
        })),
      };

    case 'user':
      if (
        !field.autoCompleteUrl ||
        field.autoCompleteUrl.includes('/rest/api/1.0/')
      ) {
        console.warn('No autoCompleteUrl, or unsupported url', field);
        return;
      }

      return {
        type: 'select',
        multiple: false,
        options: ({ searchParam }) =>
          context.getAutocompleteOptions({
            responseType: field.schema.type === 'user' ? 'user' : 'issuePicker',
            autoCompleteUrl: field.autoCompleteUrl,
            searchParam,
          }),
      };

    case 'team':
    case 'issuelink':
      console.warn('Unsupported field', field);
      return;

    default:
      console.warn('Unknown schema type', field);

      if (process.env.NODE_ENV !== 'production') {
        throw new Error(`Unknown schema type`);
      }
  }
};

export const transformJiraField =
  (context: TransformContext) =>
  (
    field: JiraIssueCreateMetaResponse['fields'][0]
  ): IntegrationRecordField | undefined => {
    const type = getFieldType(field, context);

    if (!type) {
      return;
    }

    return {
      name: field.fieldId,
      description: field.name,
      required: field.required,
      type,
    };
  };

/** transforms Jira's createmeta response into a list of fields that can
 * be passed to the whiteboard for record creation
 */
export const transformCreateMeta = (
  response: JiraIssueCreateMetaResponse,
  context: TransformContext
) => {
  return response.fields
    .filter(field => !OMIT_FIELDS.includes(field.fieldId))
    .map(transformJiraField(context))
    .filter(Boolean);
};

type TransformAutocompleteInput =
  | { responseType: 'issuePicker'; response: JiraIssuePicker }
  | { responseType: 'user'; response: JiraUserSearch };

/** transforms the response from one of Jira's provided autocompleteUrls to dropdown options,
 * according to the response type */
export const transformAutocompleteResponse = ({
  responseType,
  response,
}: TransformAutocompleteInput): DropdownOption<unknown>[] => {
  if (responseType === 'issuePicker') {
    return response.sections.flatMap(section =>
      section.issues.map(issue => ({
        value: String(issue.id),
        text: issue.summaryText,
        item: issue.id,
      }))
    );
  }

  // assume users
  return response.map(r => ({
    value: r.accountId,
    text: r.displayName,
    item: r.accountId,
  }));
};
